import { FILE_TYPE_PNG, MAX_PNG_SIZE, MAX_SAFARI_CANVAS_SIZE } from '@constants/global';

const createImage = (url: string) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

function limitSize(size: number, maximumPixels: number) {
  const requiredPixels = size * size;
  if (requiredPixels <= maximumPixels) return size;

  const scalar = Math.sqrt(maximumPixels) / Math.sqrt(requiredPixels);
  return Math.floor(size * scalar);
}

export const isLargePng = async (type: string, fileEl: { src: string; file: File }) => {
  const isPng = type.includes(FILE_TYPE_PNG);
  if (!isPng) return false;
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.src = fileEl.src;

    img.onload = () => {
      const fileWidth = img.width;
      const isBiggerSize = fileWidth > MAX_PNG_SIZE;
      resolve(isBiggerSize);
    };

    img.onerror = () => {
      // Handle image loading error here if needed
      resolve(reject);
    };
  });
};

export const getImagesList = (files: FileList): { src: string; file: File }[] => {
  const imagesList = [];
  for (let i = 0; i < files.length; i++) {
    imagesList.push({ file: files[i], src: URL.createObjectURL(files[i]) });
  }
  return imagesList;
};

export async function getCroppedImg(
  imageSrc: string,
  imgType: string,
  pixelCrop: {
    width: number;
    height: number;
    x: number;
    y: number;
  },
  rotation: number = 0
) {
  const image = (await createImage(imageSrc)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return;
  }

  const maxSize = Math.max(image.width, image.height);
  const safeArea = limitSize(2 * ((maxSize / 2) * Math.sqrt(2)), MAX_SAFARI_CANVAS_SIZE);

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      // eslint-disable-next-line
      // @ts-ignore
      const file = new File([blob], 'cropped-image', {
        lastModified: new Date(),
        type: imgType,
      });
      if (blob) {
        resolve({ src: URL.createObjectURL(blob), file });
      }
    }, imgType);
  });
}
